import { Button, Card, Checkbox, cn } from "@beweegbaas/ui";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { containerStyles, Page } from "#/components/page";
import { StartLessonSummary } from "#/modules/les.start-flow/components/summary.block";
import { getLesFlowStep1Data } from "#/modules/les.start-flow/les-flow.functions";
import { getInnerLeerjaarNumberTranslation } from "#/modules/util.i18n/enum-mapping";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/start/les-flow/1",
)({
  // Step 1 (the class picker) is skipped in edit mode — the classes are fixed.
  beforeLoad: ({ search, params }) => {
    if (search.editLesId != null) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/start/les-flow/2",
        params,
        search,
      });
    }
  },
  loader: () => getLesFlowStep1Data(),
  component: LesFlowStep1Page,
});

function LesFlowStep1Page() {
  const { klassen } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (search.klasIds.length === 0) return;
    void navigate({
      to: "/$locale/{-$schoolyear}/$school/start/les-flow/2",
      search: (prev) => prev,
    });
  };

  const toggleKlas = (klasId: string, checked: boolean) => {
    void navigate({
      search: (prev) => {
        // While another page is still loading, the router hands `prev` that page's search, which
        // has no klasIds.
        const klasIds = prev.klasIds ?? [];
        return {
          ...prev,
          klasIds: checked ? [...klasIds, klasId] : klasIds.filter((id) => id !== klasId),
        };
      },
      replace: true,
      resetScroll: false,
    });
  };

  const hasNoClasses = klassen.length === 0;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-0">
      <Page>
        <Page.Header>
          <Page.Title>{m["les.start.choose_your_group"]()}</Page.Title>
          <Page.Header.Actions>
            <Page.Header.MainAction>
              {!hasNoClasses ? (
                <Button type="submit" size="xl">
                  {m["common.actions.next"]()}
                  <IconMaterialSymbolsArrowRightAltRounded />
                </Button>
              ) : null}
            </Page.Header.MainAction>
          </Page.Header.Actions>
        </Page.Header>
        <Page.Body className={cn(containerStyles({ container: "default" }))}>
          <Page.SidebarLayout>
            <div className="grow">
              {hasNoClasses ? (
                <div className="flex flex-col items-start gap-4">
                  <p className="text-muted-foreground">
                    {m["common.empty_state.no_classes_students"]()}
                  </p>
                  <Button
                    size="xl"
                    render={
                      <Link
                        from={Route.fullPath}
                        to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                        params={(prev: Record<string, string>) => ({
                          ...prev,
                          type: "modify-existing-year",
                        })}
                      />
                    }
                  >
                    {m["common.empty_state.import_students"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </Button>
                </div>
              ) : (
                <div
                  role="group"
                  aria-label={m["les.start.select_groups_aria"]()}
                  className="flex flex-col gap-2"
                >
                  {klassen.map((classes) => {
                    const klasId = classes.id.toString();
                    const isSelected = search.klasIds.includes(klasId);
                    return (
                      <label
                        key={classes.id}
                        htmlFor={`group-${classes.id}`}
                        className="block cursor-pointer"
                      >
                        <Card
                          size="sm"
                          className={cn(
                            "flex flex-row items-start gap-6 px-6",
                            isSelected &&
                              "relative z-10 bg-gradient-to-br from-blue-100 to-blue-50 shadow-none ring-1 ring-blue-700/40 dark:bg-none dark:bg-blue-950/50 dark:ring-blue-400",
                          )}
                        >
                          <Checkbox
                            id={`group-${classes.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => toggleKlas(klasId, checked === true)}
                          />
                          <div className="flex grow justify-between">
                            <div className="-mt-1 flex flex-col gap-0">
                              <strong
                                className={cn("font-heading text-base", {
                                  "text-blue-600 dark:text-blue-300": isSelected,
                                })}
                              >
                                {m["common.fields.klas"]()} {classes.name}{" "}
                              </strong>
                              <em
                                className={cn("opacity-70", {
                                  "text-blue-600 dark:text-blue-300": isSelected,
                                })}
                              >
                                leerjaar{" "}
                                {classes.leerjaren.map(getInnerLeerjaarNumberTranslation).join("/")}
                              </em>
                            </div>
                            <em>
                              {m["les.start.student_count"]({
                                count: classes.leerlingCount,
                              })}
                            </em>
                          </div>
                        </Card>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <Page.Sidebar className="@max-4xl/sidebar-layout:hidden @6xl/sidebar-layout:max-w-[320px]">
              <StartLessonSummary className="sticky top-4" />
            </Page.Sidebar>
          </Page.SidebarLayout>
        </Page.Body>
      </Page>
    </form>
  );
}
